import { z } from "zod";

export const OffboardingStatusEnum = z.enum([
    "RETENTION_REQUESTED",
    "WITHDRAWN",
    "REJECTED",
    "RESIGNATION_PENDING",
    "RESIGNATION_SUBMITTED",
    "UNDER_REVIEW",
    "LWD_CONFIRMED",
    "CLEARANCE_IN_PROGRESS",
    "EXIT_FORM_PENDING",
    "HR_CLEARANCE_PENDING",
    "EXIT_COMPLETED",
]);

export type OffboardingStatus = z.infer<typeof OffboardingStatusEnum>;

export const OffboardingRequestSchema = z.object({
    id: z.number(),
    employeeId: z.number(),
    retentionRequested: z.boolean(),
    expectedLwd: z.string().nullish(),
    finalLwd: z.string().nullish(),
    cpoApproved: z.boolean(),
    cooApproved: z.boolean(),
    approverComment: z.string().nullish(),
    status: OffboardingStatusEnum,
    exitInterviewCompleted: z.boolean(),
    isTermination: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
    isAcknowledged: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
    version: z.number().nullish(),
    withdrawalRequested: z.boolean(),
    withdrawalReason: z.string().nullish(),
    itCompleted: z.boolean(),
    itRemarks: z.string().nullish(),
    hrCompleted: z.boolean(),
    hrRemarks: z.string().nullish(),
    payrollCompleted: z.boolean(),
    payrollRemarks: z.string().nullish(),
    adminCompleted: z.boolean(),
    adminRemarks: z.string().nullish(),
    rmCompleted: z.boolean(),
    rmRemarks: z.string().nullish(),
    payrollHold: z.boolean(),
    employeeName: z.string().nullish(),
    withdrawnDate: z.string().nullish(),
    updatedAt: z.string().nullish(),
    ceatedAt: z.string().nullish(), 
    createdAt: z.string().nullish().optional(),
    managerId: z.union([z.number(), z.string(), z.null()]).optional().transform((val) => {
        if (!val) return undefined;
        if (typeof val === "number") return val;
        if (typeof val === "string") return parseInt(val, 10);
        return undefined;
    }),
}).passthrough();

export type OffboardingRequest = z.infer<typeof OffboardingRequestSchema>;

export type OffboardingViewState =
    | "INACTIVE"
    | "RETENTION_REQUESTED"
    | "WITHDRAWN"
    | "REJECTED"
    | "RESIGNATION_FORM_PENDING"
    | "RESIGNATION_IN_PROGRESS"
    | "EXIT_COMPLETED";

export function resolveOffboardingViewState(
    request: OffboardingRequest | null,
): OffboardingViewState {
    if (!request) {
        return "INACTIVE";
    }

    switch (request.status) {
        case "RETENTION_REQUESTED":
            return "RETENTION_REQUESTED";
        case "WITHDRAWN":
            return "WITHDRAWN";
        case "REJECTED":
            return "REJECTED";
        case "RESIGNATION_PENDING":
            return "RESIGNATION_FORM_PENDING";
        case "RESIGNATION_SUBMITTED":
        case "UNDER_REVIEW":
        case "LWD_CONFIRMED":
        case "CLEARANCE_IN_PROGRESS":
        case "EXIT_FORM_PENDING":
        case "HR_CLEARANCE_PENDING":
            return "RESIGNATION_IN_PROGRESS";
        case "EXIT_COMPLETED":
            return "EXIT_COMPLETED";
        default:
            return "INACTIVE";
    }
}
